import React, { useState } from "react";
import { Plus, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { usePatients } from "../hooks/usePatients";
import PatientTable from "../components/PatientTable";
import PatientForm from "../components/PatientForm";

import {
  Button,
  Modal,
  Alert,
  Spinner,
} from "../../../shared/components";

const PatientListPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchText, setSearchText] = useState("");

  const {
    patients,
    loading,
    error,
    pagination,
    updateFilters,
    changePage,
    createPatient,
    updatePatient,
    deletePatient,
  } = usePatients();

/*------Search action--*/
const handleInputChange = (e) => {
  setSearchText(e.target.value);
};

const handleSearchClick = () => {
  handleSearch(searchText);
};
  /* -----------------------------
      CRUD ACTIONS
  ------------------------------*/

const handleSelect=(patient)=>{}
const navigate = useNavigate();

const handleView = (patient) => {
  navigate(`/patients/${patient.id}`);
};


  const handleCreate = () => {
    setSelectedPatient(null);
    setModalMode("create");
    setShowModal(true);
  };

  const handleEdit = (patient) => {
    setSelectedPatient(patient);
    setModalMode("edit");
    setShowModal(true);
  };

  const handleDelete = (patient) => {
    setDeleteConfirm(patient);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    const result = await deletePatient(deleteConfirm.id);

    if (result.success) {
      setDeleteConfirm(null);
    } else {
      alert(result.error);
    }
  };

  const handleSubmit = async (formData) => {
    let result;

    if (modalMode === "create") {
      result = await createPatient(formData);
    } else {
      result = await updatePatient(selectedPatient.id, formData);
    }

    if (result.success) {
      setShowModal(false);
      setSelectedPatient(null);
    } else {
      alert(result.error);
    }
  };

  /* -----------------------------
      FILTERS
  ------------------------------*/

  // const handleSearch = (e) => {
  //   updateFilters({ search: e.target.value });
  // };

  const handleSearch = (text) => {
  updateFilters({ search: text });
};
const clearSearch = () => {
  setSearchText("");
  updateFilters({ search: "" });
};

  const handleFilterChange = (e) => {
    updateFilters({
      [e.target.name]: e.target.value,
    });
  };

  /* -----------------------------
      ERROR STATE
  ------------------------------*/

  if (error) {
    return <Alert variant="error" message={error} />;
  }

  /* -----------------------------
      RENDER
  ------------------------------*/

  return (
    <div className="container mx-auto px-4 py-8">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Patients
        </h1>

        <Button variant="primary" onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
        </Button>
      </div>

      {/* FILTERS */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-col md:flex-row gap-4">

        {/* <input
          type="text"
          placeholder="Search patients..."
          onChange={handleSearch}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
        /> */}
        <div className="relative flex-1">

  <input
    type="text"
    placeholder="Search patients..."
    value={searchText}
    onChange={handleInputChange}
    onKeyDown={(e) => {
      if (e.key === "Enter" && searchText.trim()) {
        handleSearchClick();
      }
    }}
    className="w-full pr-20 pl-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
  />

  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">

    {/* Search Button */}
    <button
      onClick={handleSearchClick}
      disabled={!searchText.trim()}
      className={`p-1 rounded transition 
        ${
          searchText.trim()
            ? "text-gray-500 hover:text-primary-600"
            : "text-gray-300 cursor-not-allowed"
        }`}
    >
      <Search className="h-4 w-4" />
    </button>

    {/* Clear Button */}
    {searchText && (
      <button
        onClick={clearSearch}
        className="p-1 rounded text-gray-400 hover:text-red-500 transition"
      >
        <X className="h-4 w-4" />
      </button>
    )}

  </div>

</div>
        <select
          name="status"
          onChange={handleFilterChange}
          className="px-4 py-2 border border-gray-300 rounded-md"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <select
          name="gender"
          onChange={handleFilterChange}
          className="px-4 py-2 border border-gray-300 rounded-md"
        >
          <option value="">All Gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>

      </div>

      {/* TABLE */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <>
          <PatientTable
            patients={patients}
            onView={handleView}
            onSelectPatient={handleSelect}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />

          {/* PAGINATION */}
          <div className="mt-6 flex justify-between items-center">

            <div className="text-sm text-gray-600">
              
              Showing{" "}
              {(pagination.currentPage - 1) * pagination.perPage + 1}
              {" "}to{" "}
              {Math.min(
                pagination.currentPage * pagination.perPage,
                pagination.total
              )}
              {" "}of {pagination.total} patients
            </div>

            <div className="flex space-x-2">

              <Button
                variant="outline"
                size="sm"
                disabled={pagination.currentPage === 1}
                onClick={() =>
                  changePage(pagination.currentPage - 1)
                }
              >
                Previous
              </Button>

              <span className="px-3 py-2 text-sm">
                Page {pagination.currentPage} of{" "}
                {Math.ceil(
                  pagination.total / pagination.perPage
                )}
              </span>

              <Button
                variant="outline"
                size="sm"
                disabled={
                  pagination.currentPage ===
                  Math.ceil(
                    pagination.total / pagination.perPage
                  )
                }
                onClick={() =>
                  changePage(pagination.currentPage + 1)
                }
              >
                Next
              </Button>

            </div>
          </div>
        </>
      )}

      {/* CREATE / EDIT MODAL */}

     {/* CREATE / EDIT MODAL */}
<Modal
  isOpen={showModal}
  onClose={() => setShowModal(false)}
  title={modalMode === "create" ? "Add Patient" : "Edit Patient"}
  size="xl"
>
  <div className="max-h-[80vh] overflow-y-auto">
    <PatientForm
      initialData={selectedPatient}
      onSubmit={handleSubmit}
      onCancel={() => setShowModal(false)}
    />
  </div>
</Modal>

      {/* DELETE CONFIRMATION */}

      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Patient"
      >
        <div className="p-4">

          <p className="mb-6 text-gray-700">
            Are you sure you want to delete{" "}
            <strong>
              {deleteConfirm?.first_name}{" "}
              {deleteConfirm?.last_name}
            </strong>
            ?
          </p>

          <div className="flex justify-end gap-3">

            <Button
              variant="secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </Button>

            <Button
              variant="danger"
              onClick={confirmDelete}
            >
              Delete
            </Button>

          </div>
        </div>
      </Modal>

    </div>
  );
};

export default PatientListPage;